import { FC, useMemo, useState, useReducer, useEffect, useLayoutEffect, ChangeEvent } from 'react';
import { Field, Form, Label } from '../../Registration.styles';
import { Dropdown, Input, Loading, Skeleton } from '../../../../components';
import { RegistrationFormPage } from '../../templates';
import { newYearFormInitialState, newYearFormReducer } from './newYear.form';
import { Option } from '../../../../components/dropdown/Dropdown';
import { useToastStore } from '../../../../stores';
import { isFormValid } from '../../../../utils';
import { getVehicleBrands, getVehicleModels } from '../../../../services';

const NewYear: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [tiposDeVeiculo, setTiposDeVeiculo] = useState<Option[]>([]);
	const [marcasDeVeiculo, setMarcasDeVeiculo] = useState<Option[]>([]);
	const [procurandoMarcas, setProcurandoMarcas] = useState<boolean>(false);
	const [modelosDeVeiculo, setModelosDeVeiculo] = useState<Option[]>([]);
	const [procurandoModelos, setProcurandoModelos] = useState<boolean>(false);
	const [state, dispatch] = useReducer(newYearFormReducer, newYearFormInitialState);
	const tipoDeVeiculoSelecionado = useMemo(
		() => tiposDeVeiculo.find((veiculo) => veiculo.value === state.idVeiculoTipo),
		[state.idVeiculoTipo, tiposDeVeiculo]
	);
	const marcaDeVeiculoSelecionada = useMemo(
		() => marcasDeVeiculo.find((marca) => marca.value === state.idVeiculoMarca),
		[marcasDeVeiculo, state.idVeiculoMarca]
	);
	const modeloSelecionado = useMemo(
		() => modelosDeVeiculo.find((modelo) => modelo.value === state.idVeiculoModelo),
		[modelosDeVeiculo, state.idVeiculoModelo]
	);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
	};

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
	};

	const handleSubmit = () => {
		if (!isFormValid(state)) {
			setToast({ message: 'Preencha todos os campos', variant: 'error' });
			return;
		}

	};

	const resetSelectedOptions = (fields: string[]) => {
		fields.forEach((field) => {
			dispatch({
				type: 'SET_SELECT_OPTION',
				field: field,
				value: 0,
			});
		});
	};

	useLayoutEffect(() => {
		const fetchTipos = async () => {
			setLoading(true);

			try {
				setTiposDeVeiculo([
					{ value: 1, label: 'Carro' },
					{ value: 2, label: 'Moto' },
					{ value: 3, label: 'Caminhão/Ônibus' },
				]);
			} catch {
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchTipos();
	}, []);

	useEffect(() => {
		const fetchMarcas = async () => {
			if (!state.idVeiculoTipo) return;

			setProcurandoMarcas(true);
			setProcurandoModelos(true);

			try {
				const response = await getVehicleBrands(state.idVeiculoTipo);

				const opcoesDeMarca: Option[] = response.map((marca) => ({
					value: marca.id,
					label: marca.veiculoMarca ?? '',
				}));

				setMarcasDeVeiculo(opcoesDeMarca);

				const opcoesContemMarcaSelecionada =
					opcoesDeMarca.findIndex((marca) => marca.value === state.idVeiculoMarca) !== -1;

				if (!opcoesContemMarcaSelecionada) {
					resetSelectedOptions(['idVeiculoMarca', 'idVeiculoModelo']);
					setModelosDeVeiculo([]);
				}
			} catch {
				setToast({ message: 'Erro ao buscar marcas de veículo.', variant: 'error' });
			} finally {
				setTimeout(() => {
					setProcurandoMarcas(false);
					setProcurandoModelos(false);
				}, 500);
			}
		};

		fetchMarcas();
	}, [setToast, state.idVeiculoMarca, state.idVeiculoTipo]);

	useEffect(() => {
		const fetchModelos = async () => {
			if (!state.idVeiculoMarca) return;

			setProcurandoModelos(true);

			try {
				const response = await getVehicleModels(state.idVeiculoMarca);

				const opcoesDeModelo: Option[] = response.map((modelo) => ({
					value: modelo.id,
					label: modelo.veiculoModelo ?? '',
				}));

				setModelosDeVeiculo(opcoesDeModelo);

				const opcoesContemModeloSelecionado =
					opcoesDeModelo.findIndex((modelo) => modelo.value === state.idVeiculoModelo) !== -1;

				if (!opcoesContemModeloSelecionado) {
					resetSelectedOptions(['idVeiculoModelo']);
				}
			} catch {
				setToast({ message: 'Erro ao buscar modelos de veículo.', variant: 'error' });
			} finally {
				setTimeout(() => {
					setProcurandoModelos(false);
				}, 500);
			}
		};

		fetchModelos();
	}, [setToast, state.idVeiculoMarca, state.idVeiculoModelo]);

	if (loading) return <Loading fullScreen />;

	return (
		<RegistrationFormPage title='Cadastrar Ano' onSubmit={handleSubmit}>
			<Form>
				<Label>Tipo</Label>
				<Field>
					<Dropdown
						options={tiposDeVeiculo}
						selected={tipoDeVeiculoSelecionado}
						onSelect={(option: Option) => handleSelectOption('idVeiculoTipo', option)}
					/>
				</Field>
				<Label>Marca</Label>
				<Field>
					{procurandoMarcas ? (
						<Skeleton $borderRadius={12} />
					) : (
						<Dropdown
							searchable
							options={marcasDeVeiculo}
							selected={marcaDeVeiculoSelecionada}
							onSelect={(option: Option) => handleSelectOption('idVeiculoMarca', option)}
							disabled={marcasDeVeiculo.length === 0}
						/>
					)}
				</Field>
				<Label>Modelo</Label>
				<Field>
					{procurandoModelos ? (
						<Skeleton $borderRadius={12} />
					) : (
						<Dropdown
							searchable
							options={modelosDeVeiculo}
							selected={modeloSelecionado}
							onSelect={(option: Option) => handleSelectOption('idVeiculoModelo', option)}
							disabled={modelosDeVeiculo.length === 0}
						/>
					)}
				</Field>
				<Label>Ano</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Ano'
						value={state.ano}
						onChange={handleInputChange}
						name='ano'
					/>
				</Field>
				<Label>Combustível</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Combustível'
						value={state.combustivel}
						onChange={handleInputChange}
						name='combustivel'
					/>
				</Field>
				<Label>Código FIPE</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Código FIPE'
						value={state.codigoFipe}
						onChange={handleInputChange}
						name='codigoFipe'
					/>
				</Field>
				<Label>Valor FIPE</Label>
				<Field>
					<Input
						variant='text'
						placeholder='Valor FIPE'
						value={state.valorFipe}
						onChange={handleInputChange}
						name='valorFipe'
					/>
				</Field>
			</Form>
		</RegistrationFormPage>
	);
};

export default NewYear;
